import { Component, EventEmitter, Input, Output, computed, effect, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { IUser, UserPermission } from 'src/app/core/models/user.model';
import { getAuthStateSelect } from 'src/app/core/store/auth/auth.selector';
import { MedicalRecordState } from 'src/app/features/medical-record/store/medical-record.state';
import { environment } from 'src/environments/environment';
import { MedicalRecordService, PermissionType } from 'src/app/features/medical-record/services/medical-record.service';
import { QuestionnairesService } from 'src/app/features/medical-record/services/questionnaires.service';
import { Alerts } from '../alerts/alert';
import { LoadingService } from '../loading/loading.service';
import { PatientService } from '../../services/patient/patient-info.service';
import { Router } from '@angular/router';
import _ from 'lodash';
import { TabsStateService } from 'src/app/features/medical-record/services/tabs-state.service';
import { PatientInfoAPI } from 'src/app/features/medical-record/medical-record-page/pages/patient-informations/model/patient.model';

@Component({
  selector: 'app-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss']
})
export class FooterComponent {

  version: string = environment.version;
  @Input() userLogged: IUser | null = null;
  @Output() cancel = new EventEmitter();
  @Output() remove = new EventEmitter();
  @Output() newClickEvent = new EventEmitter();
  permissions?: UserPermission;
  medicalRecordIsOpen: boolean = false;
  protected patient!: PatientInfoAPI;

  loadingPatientInfo = this.patientInfo.isLoadingPatientSignal

  loading = computed(() => {
    const footerLoading = !!this.loadingService.loadingState().footer;
    const patientLoading = !!this.patientInfo.isLoadingPatientSignal
    return footerLoading && patientLoading;
  })

  isSomeMedicalTabLoading = computed(() => {
    const isSomeMedicalTabLoading = this.tabsState.medicalTabsLoading().length > 0
    return isSomeMedicalTabLoading
  })

  constructor(
    protected medicalRecordService: MedicalRecordService,
    public loadingService: LoadingService,
    private alerts: Alerts,
    private router: Router,
    private store: Store<MedicalRecordState>,
    private patientInfo: PatientService,
    private tabsState: TabsStateService,
  ) {
    this.store
      .select(getAuthStateSelect)
      .subscribe((user) => {
        this.permissions = user?.user?.permission;
      });

    (this.medicalRecordService.isMedicalRecordOpen
      .subscribe(permission => {
        this.medicalRecordIsOpen = permission;
      }));
  }

  ngOnInit() {
    this.patientInfo.selectedPatientObs.subscribe(patient => {
      if (patient) {
        this.patient = patient;
      }
    });
  }

  isPatientDead(): boolean {
    if (this.patient && this.patient.patientStatus && this.patient.patientStatus.dataObito) {
      if (this.patient.patientStatus.dataObito !== null && this.patient.patientStatus.dataObito !== undefined) {
        return true;
      }
      return false;
    }
    return false;
  }

  async cancelMedicalRecordChanges() {

    const { isConfirmed } = await this.alerts.alertWithTwoButtons('warning',
      'Descartando Atendimento',
      'Tem certeza que você deseja fechar o Prontuário do paciente sem registrar as alterações realizadas até agora? Ao descartar o atendimento você será direcionado para a Agenda do Dia.', "Descartar Atendimento", "Cancelar");

    if (isConfirmed) {
      await this.medicalRecordService.cancelAllChanges();
      this.router.navigate(['schedule']);
    }
    else {
      return;
    }
  }

  async showCantSaveAlert({ hasAnamnesis, hasconduct, allDiagnosisWithDate }: { hasAnamnesis: boolean, hasconduct: boolean, allDiagnosisWithDate: boolean }) {

    const questConditions: string[] = [];
    let errorMsg: string[] = [];
    if (!hasconduct) {
      questConditions.push('Conduta');
    }
    if (!hasAnamnesis) {
      questConditions.push('Anamnese');
    }
    if (questConditions.length > 0) {
      errorMsg.push(
        `Durante o primeira atendimento é necessário adicionar uma resposta de Conduta e Anamnesis para salvar.\nEsse prontuário ainda não possui: ${questConditions.join(' e ')}`
      );
    }
    if (!allDiagnosisWithDate) {
      errorMsg.push(`Um diagnóstico adicionado ou atualizado nesse atendimento está sem data.`);
    }
    await this.alerts.alertWithOneButton(
      'error',
      'Operação Não Permitida',
      `${errorMsg}\n
        Adicione as informações nas respectivas abas para então poder salvar e finalizar esse atendimento.`,
      'Ok'
    );
  }

  async save() {
    try {
      const { canSave, hasAnamnesis, hasconduct, allDiagnosisWithDate } = await this.medicalRecordService.canSaveMedicalRecord();
      if (!canSave) {
        this.showCantSaveAlert({ hasAnamnesis, hasconduct, allDiagnosisWithDate });
        return;
      }
      const { totalSum } = await this.medicalRecordService.nonQuestionariesMRtoSave()
      const { isConfirmed } = await this.alerts
        .alertWithTwoButtons(
          'warning',
          'Finalizar Atendimento',
          'Tem certeza que você deseja finalizar o atendimento deste paciente?',
          'Finalizar Atendimento',
          'Cancelar'
        );

      if (!isConfirmed) {
        return;
      }

      const { diagToSaveOk, diagToUpdateOk, questStatus } = await this.medicalRecordService.saveAllMedicalRecords(true);
      let errorMsg = [];

      if (questStatus === 'error') {
        errorMsg.push('Não foi possível salvar os questionários do paciente.');
      }
      if (!diagToUpdateOk) {
        errorMsg.push('Não foi possível atualizar o(s) diagnóstico(s) do paciente.');
      }
      if (!diagToSaveOk) {
        errorMsg.push('Não foi possível salvar o novo diagnóstico.');
      }
      if (errorMsg.length) {
        errorMsg.push('Tente novamente mais tarde ou entre em contato com os administradores do sistema.');

        this.alerts.alertWithOneButton(
          'error',
          'Erro',
          errorMsg.join('\n'),
          'Ok'
        );
        return;
      }

      if (questStatus === 'noNewInfo' && totalSum == 0) {
        this.alerts.alertWithOneButton(
          'success',
          'Prontuário já está atualizado',
          'Não haviam informações novas para serem salvas.',
          'Ok'
        );
        return;
      }
      this.alerts.alertWithOneButton(
        'success',
        'Prontuário salvo',
        'As informações inseridas no prontuário do(a) paciente foram salvas com sucesso!',
        'Ok'
      ).then(() => {
        this.router.navigate(['schedule']);
      })
    }
    catch (e) {
      this.alerts.alertWithOneButton(
        'error',
        'Erro ao Registrar Atendimento',
        'As informações incluídas/alteradas não foram salvas. Tente novamente.',
        'Ok'
      );
    }
  }

  async closeMedicalRecord() {
    const { isConfirmed } = await this.alerts
      .alertWithTwoButtons(
        'warning',
        'Fechar Prontuário',
        'Deseja sair do Prontuário? Você será direcionado para a Agenda do dia.',
        "Fechar Prontuário",
        'Cancelar'
      );

    if (isConfirmed) {
      this.router.navigate(['schedule']);
    }
    else {
      return;
    }
  }

  isUpdatingMedicalRecord() {
    if(this.userLogged){

    }
  }

  startNewMedicalRecord() {
    this.newClickEvent.emit();
  }
}
